import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Activity, Wifi, AlertTriangle, TrendingUp, RefreshCw,
  Loader2, Shield, Zap, Database, ChevronDown, ChevronUp, Clock
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LABELS = {
  it: { title: 'Monitor Dati', subtitle: 'Controlla il consumo delle tue eSIM', noEsims: 'Nessuna eSIM attiva', noEsimsDesc: 'Acquista un piano eSIM per vedere il consumo dati.', refresh: 'Aggiorna', loading: 'Caricamento dati...', usage: 'Consumo Dati', plan: 'Piano', expires: 'Scade il', active: 'Attivo', assigned: 'Assegnato', warning80: 'Hai usato oltre l\'80% dei dati! Valuta un top-up.', warning95: 'Dati quasi esauriti! Effettua un top-up subito.', safetyTitle: 'Avvisi di Sicurezza', safetyLoading: 'Caricamento avvisi...', safetyNone: 'Seleziona una eSIM per vedere gli avvisi di sicurezza della destinazione.', unlimited: 'Illimitato', dataLeft: 'rimanenti', noUsageData: 'Dati consumo non disponibili' },
  en: { title: 'Data Monitor', subtitle: 'Track your eSIM data usage', noEsims: 'No active eSIMs', noEsimsDesc: 'Purchase an eSIM plan to track data usage.', refresh: 'Refresh', loading: 'Loading data...', usage: 'Data Usage', plan: 'Plan', expires: 'Expires', active: 'Active', assigned: 'Assigned', warning80: 'You\'ve used over 80% of your data! Consider a top-up.', warning95: 'Data almost depleted! Top up now.', safetyTitle: 'Safety Alerts', safetyLoading: 'Loading safety info...', safetyNone: 'Select an eSIM to see safety alerts for the destination.', unlimited: 'Unlimited', dataLeft: 'remaining', noUsageData: 'Usage data unavailable' },
  pl: { title: 'Monitor Danych', subtitle: 'Śledź zużycie danych eSIM', noEsims: 'Brak aktywnych eSIM', noEsimsDesc: 'Kup plan eSIM, aby śledzić zużycie danych.', refresh: 'Odśwież', loading: 'Ładowanie danych...', usage: 'Zużycie Danych', plan: 'Plan', expires: 'Wygasa', active: 'Aktywny', assigned: 'Przypisany', warning80: 'Wykorzystano ponad 80% danych! Rozważ doładowanie.', warning95: 'Dane prawie wyczerpane! Doładuj teraz.', safetyTitle: 'Alerty Bezpieczeństwa', safetyLoading: 'Ładowanie alertów...', safetyNone: 'Wybierz eSIM, aby zobaczyć alerty bezpieczeństwa.', unlimited: 'Bez limitu', dataLeft: 'pozostało', noUsageData: 'Dane o zużyciu niedostępne' },
  fr: { title: 'Moniteur de Données', subtitle: 'Suivez votre consommation eSIM', noEsims: 'Aucune eSIM active', noEsimsDesc: 'Achetez un plan eSIM pour suivre la consommation.', refresh: 'Actualiser', loading: 'Chargement...', usage: 'Consommation', plan: 'Forfait', expires: 'Expire le', active: 'Actif', assigned: 'Assigné', warning80: 'Vous avez utilisé plus de 80% de vos données ! Pensez à un top-up.', warning95: 'Données presque épuisées ! Rechargez maintenant.', safetyTitle: 'Alertes de Sécurité', safetyLoading: 'Chargement des alertes...', safetyNone: 'Sélectionnez une eSIM pour voir les alertes.', unlimited: 'Illimité', dataLeft: 'restant', noUsageData: 'Données de consommation indisponibles' },
  es: { title: 'Monitor de Datos', subtitle: 'Controla el consumo de tus eSIM', noEsims: 'Sin eSIM activas', noEsimsDesc: 'Compra un plan eSIM para ver el consumo.', refresh: 'Actualizar', loading: 'Cargando...', usage: 'Consumo', plan: 'Plan', expires: 'Vence', active: 'Activo', assigned: 'Asignado', warning80: 'Has usado más del 80% de tus datos. Considera un top-up.', warning95: '¡Datos casi agotados! Recarga ahora.', safetyTitle: 'Alertas de Seguridad', safetyLoading: 'Cargando alertas...', safetyNone: 'Selecciona una eSIM para ver las alertas.', unlimited: 'Ilimitado', dataLeft: 'restante', noUsageData: 'Datos de consumo no disponibles' },
  de: { title: 'Daten-Monitor', subtitle: 'eSIM-Datenverbrauch verfolgen', noEsims: 'Keine aktiven eSIMs', noEsimsDesc: 'Kaufen Sie einen eSIM-Plan.', refresh: 'Aktualisieren', loading: 'Laden...', usage: 'Datenverbrauch', plan: 'Plan', expires: 'Läuft ab', active: 'Aktiv', assigned: 'Zugewiesen', warning80: 'Über 80% verbraucht! Top-up empfohlen.', warning95: 'Daten fast aufgebraucht! Jetzt aufladen.', safetyTitle: 'Sicherheitshinweise', safetyLoading: 'Laden...', safetyNone: 'Wählen Sie eine eSIM für Sicherheitshinweise.', unlimited: 'Unbegrenzt', dataLeft: 'übrig', noUsageData: 'Verbrauchsdaten nicht verfügbar' },
  ru: { title: 'Монитор Данных', subtitle: 'Отслеживание использования eSIM', noEsims: 'Нет активных eSIM', noEsimsDesc: 'Купите план eSIM.', refresh: 'Обновить', loading: 'Загрузка...', usage: 'Использование', plan: 'План', expires: 'Истекает', active: 'Активно', assigned: 'Назначено', warning80: 'Использовано более 80%! Рассмотрите пополнение.', warning95: 'Данные почти исчерпаны! Пополните сейчас.', safetyTitle: 'Предупреждения', safetyLoading: 'Загрузка...', safetyNone: 'Выберите eSIM для просмотра предупреждений.', unlimited: 'Безлимит', dataLeft: 'осталось', noUsageData: 'Данные о потреблении недоступны' },
};

function UsageBar({ used, total, label }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const color = pct >= 95 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-teal-500';
  const glow = pct >= 95 ? 'shadow-rose-500/30' : pct >= 80 ? 'shadow-amber-500/30' : 'shadow-teal-500/30';

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-slate-400">{label}</span>
        <span className="text-white font-mono">{used.toFixed(1)} / {total > 0 ? total.toFixed(1) : '∞'} GB</span>
      </div>
      <div className="h-2.5 bg-white/[0.06] rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full rounded-full ${color} shadow-md ${glow}`}
        />
      </div>
    </div>
  );
}

function SafetyPanel({ country, lang, labels }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!country) return;
    setLoading(true);
    setInfo(null);
    axios.get(`${API}/safety/country/${encodeURIComponent(country)}`, { params: { lang } })
      .then(res => setInfo(res.data.safety_info))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [country, lang]);

  if (!country) {
    return (
      <div className="text-center py-8 text-slate-500 text-sm">
        <Shield className="w-8 h-8 mx-auto mb-2 opacity-40" />
        {labels.safetyNone}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> {labels.safetyLoading}
      </div>
    );
  }

  if (!info) return null;

  const lines = info.split('\n').filter(l => l.trim());
  const preview = lines.slice(0, 6);
  const rest = lines.slice(6);

  const renderLine = (line, i) => {
    let html = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>');
    if (line.startsWith('- ') || line.startsWith('• ')) {
      html = '<span class="text-amber-400 mr-1.5">•</span>' + html.slice(2);
    }
    if (/^\d+\.\s/.test(line)) {
      const num = line.match(/^(\d+)\./)[1];
      html = `<span class="text-amber-400 font-mono mr-1.5">${num}.</span>` + line.replace(/^\d+\.\s/, '');
    }
    return <p key={i} className="text-sm text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <div className="space-y-2">
      {preview.map(renderLine)}
      <AnimatePresence>
        {expanded && rest.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="space-y-2 overflow-hidden">
            {rest.map((l, i) => renderLine(l, i + 6))}
          </motion.div>
        )}
      </AnimatePresence>
      {rest.length > 0 && (
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300 transition-colors mt-1">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Show less' : `Show more (${rest.length} lines)`}
        </button>
      )}
    </div>
  );
}

export default function DataMonitor({ onNavigate }) {
  const { i18n } = useTranslation();
  const lang = LABELS[i18n.language] ? i18n.language : 'en';
  const L = LABELS[lang];

  const [esims, setEsims] = useState([]);
  const [usageData, setUsageData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchEsims = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/esim/my-esims`, { headers });
      const list = (res.data.esims || []).filter(e => e.sim_id);
      setEsims(list);
      if (list.length > 0 && !selectedCountry) {
        setSelectedCountry(list[0].country);
      }
      return list;
    } catch { return []; }
  }, []);

  const fetchUsage = useCallback(async (simId) => {
    try {
      const res = await axios.get(`${API}/esim/${simId}/usage`, { headers });
      return res.data;
    } catch { return null; }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const list = await fetchEsims();
    const usageMap = {};
    await Promise.all(list.map(async (esim) => {
      const data = await fetchUsage(esim.sim_id);
      if (data) usageMap[esim.sim_id] = data;
    }));
    setUsageData(usageMap);
    setLoading(false);
    setRefreshing(false);
  }, [fetchEsims, fetchUsage]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAll();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
        <span>{L.loading}</span>
      </div>
    );
  }

  if (esims.length === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center" data-testid="no-esims-monitor">
        <Activity className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <h3 className="text-white font-semibold mb-2">{L.noEsims}</h3>
        <p className="text-slate-400 text-sm mb-6">{L.noEsimsDesc}</p>
        <button onClick={() => onNavigate('shop')} className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-6 py-2.5 rounded-full transition-all" data-testid="monitor-shop-btn">
          Shop eSIM
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-monitor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-teal-400" /> {L.title}
          </h2>
          <p className="text-slate-400 text-sm mt-0.5">{L.subtitle}</p>
        </div>
        <button onClick={handleRefresh} className="flex items-center gap-1.5 text-slate-400 hover:text-teal-400 text-sm transition-colors px-3 py-1.5 rounded-lg hover:bg-white/5" data-testid="monitor-refresh">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {L.refresh}
        </button>
      </div>

      {/* eSIM Cards with Usage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {esims.map(esim => {
          const usage = usageData[esim.sim_id];
          const activePkg = usage?.packages?.find(p => p.status === 'in_use');
          const totalGb = activePkg ? (activePkg.data_quantity < 0 ? -1 : activePkg.data_quantity) : 0;

          // Calculate used GB: prefer package rem_data_quantity, fallback to usage object
          let usedGb = 0;
          if (activePkg?.rem_data_quantity != null && totalGb > 0) {
            usedGb = totalGb - activePkg.rem_data_quantity;
          } else if (usage?.usage?.rem_data_quantity != null && totalGb > 0) {
            usedGb = totalGb - usage.usage.rem_data_quantity;
          }
          usedGb = Math.max(0, usedGb);
          const pct = totalGb > 0 ? (usedGb / totalGb) * 100 : 0;

          return (
            <motion.div key={esim.sim_id}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className={`bg-white/5 border rounded-2xl p-5 transition-all cursor-pointer hover:border-teal-500/30 ${selectedCountry === esim.country ? 'border-teal-500/40' : 'border-white/10'}`}
              onClick={() => setSelectedCountry(esim.country)}
              data-testid={`monitor-esim-${esim.sim_id}`}>
              
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-500/10 rounded-xl flex items-center justify-center">
                    <Wifi className="w-5 h-5 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">{esim.product_name}</h3>
                    <p className="text-slate-500 text-xs mt-0.5">{esim.country || 'Unknown'}</p>
                  </div>
                </div>
                {activePkg && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">
                    {L.active}
                  </span>
                )}
              </div>

              {/* Usage Bar */}
              {activePkg ? (
                <div className="space-y-3">
                  <UsageBar
                    used={usedGb}
                    total={totalGb > 0 ? totalGb : 0}
                    label={totalGb < 0 ? L.unlimited : L.usage}
                  />

                  {/* Expiry date with countdown */}
                  {(() => {
                    const expiryStr = activePkg.date_expiry || activePkg.end_time;
                    if (!expiryStr) return null;
                    const expiry = new Date(expiryStr.replace(' ', 'T'));
                    const now = new Date();
                    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                    const isUrgent = daysLeft <= 1;
                    const isWarning = daysLeft <= 3;
                    const isSoon = daysLeft <= 5;
                    const color = isUrgent ? 'text-rose-400' : isWarning ? 'text-amber-400' : isSoon ? 'text-orange-300' : 'text-slate-400';
                    const bg = isUrgent ? 'bg-rose-500/10 border-rose-500/20' : isWarning ? 'bg-amber-500/10 border-amber-500/20' : '';
                    return (
                      <div className={`flex items-center gap-2 text-xs ${bg ? `${bg} border rounded-xl px-3 py-2` : ''}`}>
                        <Clock className={`w-3 h-3 ${color} flex-shrink-0`} />
                        <span className={color}>
                          {L.expires}: {expiry.toLocaleDateString()} 
                          {daysLeft > 0 
                            ? ` (${daysLeft} ${daysLeft === 1 ? 'giorno' : 'giorni'})` 
                            : daysLeft === 0 ? ' (Scade oggi!)' : ' (Scaduta)'}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Remaining data info */}
                  {activePkg.rem_data_quantity != null && (
                    <p className="text-xs text-teal-400 flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      {activePkg.rem_data_quantity.toFixed(2)} {activePkg.rem_data_unit || 'GB'} {L.dataLeft}
                    </p>
                  )}

                  {/* Alerts */}
                  {pct >= 95 && (
                    <div className="flex items-center gap-2 bg-rose-500/10 border border-rose-500/20 rounded-xl px-3 py-2 mt-2">
                      <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                      <p className="text-xs text-rose-300">{L.warning95}</p>
                    </div>
                  )}
                  {pct >= 80 && pct < 95 && (
                    <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2 mt-2">
                      <TrendingUp className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <p className="text-xs text-amber-300">{L.warning80}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-xs">{L.noUsageData}</p>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Safety Alerts Panel */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="safety-alerts-panel">
        <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-amber-400" /> {L.safetyTitle}
          {selectedCountry && <span className="text-teal-400 text-sm font-normal ml-1">— {selectedCountry}</span>}
        </h3>
        <SafetyPanel country={selectedCountry} lang={lang} labels={L} />
      </div>
    </div>
  );
}
